import dayjs from "dayjs";
import { type IHistoryItem } from "src/interfaces";
import { getEntryStatusLabel } from "src/utils/entryStatus";

import { type IHistoryRow } from "../HistoryModal.types";
/**
 * Maps the history data to a list of `IHistoryItem` objects.
 *
 * @param data - The raw data containing history information.
 * @returns An array of `IHistoryItem` objects.
 */
export const mapHistory = (data: any): IHistoryItem[] => {
  return (data.history as IHistoryItem[]) ?? (data.list as IHistoryItem[]);
};

const types: Record<string, string> = {
  createEntry: "New Entry",
  saveEntry: "Edit",
  deleteFile: "File Deleted",
  deleteEntry: "Withdrawn",
  "re-verify": "Verified",
  fileUpload: "File Added",
  closeEntry: "Verified",
  primary: "Primary Verifier",
  secondary: "Secondary Verifier",
  autoSaveEntry: "Auto Save",
  "accept/autosave": "User Accepted Auto Save Warning",
  acknowledgeLatestChanges: "Acknowledged Latest Changes",
  entryDeletedRow: "User Deleted Row In Entry",
  removedRow: "Removed Row",
  removedBlankRows: "Removed rows as no data has been entered!",
  restoredRow: "Restored Row",
};

const excludedViewActions = [
  "checklist",
  "acknowledge",
  "automated",
  "primary",
  "secondary",
  "manual",
];

/**
 * Determines if a history item is an auto-save entry.
 *
 * @param historyItem - The history item to check.
 * @returns A boolean indicating whether the history item is an auto-save entry.
 */
export const isAutoSave = (historyItem: IHistoryItem) =>
  historyItem.action === "autoSaveEntry" ||
  historyItem.action === "accept/autosave" ||
  historyItem.reason === "autoSaveEntry" ||
  historyItem.reason === "accept/autosave";

const getRowTimestamp = (f4: IHistoryRow["f4"]): number => {
  if (!f4) return 0;
  if (typeof f4 === "string") {
    const parsed = dayjs(f4);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }
  const utc = f4.dateUTC ? dayjs(f4.dateUTC) : null;
  const local = f4.date ? dayjs(f4.date) : null;
  const date = utc?.isValid() ? utc : local;
  return date?.isValid() ? date.valueOf() : 0;
};

/**
 * Processes and formats history data into rows for display.
 *
 * @param historyList - An array of history items to process.
 * @returns An array of `IHistoryRow` objects representing the formatted history rows.
 */
export const getHistoryRowsData = (historyList: IHistoryItem[]) => {
  const list: IHistoryRow[] = [];
  const audit: IHistoryRow[] = [];
  historyList.forEach(historyItem => {
    let f1: string | undefined = historyItem.action
      ? types[historyItem.action]
      : "";
    let f2: string | undefined = historyItem.rolename;
    let f3: string | undefined = historyItem.full_name;
    let f4: string | { date: string; dateUTC: string } | undefined =
      historyItem.updated;
    let hid: string | undefined = historyItem.hid;
    if (historyItem.cardid) {
      list.push({
        f1: "Edited Medical Statement",
        f2,
        f3,
        f4: dayjs(historyItem.datecreated).format("D MMM YYYY - HH:mm"),
        cardid: historyItem.cardid,
        view: historyItem.nullData ? false : true,
      });
    }
    if (historyItem.action) {
      f1 = types[historyItem.action] ?? historyItem.action;
      f2 = historyItem.rolename ? historyItem.rolename : "";
      f3 = historyItem.name
        ? historyItem.name
        : historyItem.full_name
          ? historyItem.full_name
          : "";
      f4 = {
        date:
          "dateLT" in historyItem
            ? (historyItem.dateLT ?? "")
            : (historyItem.date ?? ""),

        dateUTC:
          "dateutc" in historyItem || "dateUTC" in historyItem
            ? (historyItem.dateutc ?? historyItem.dateUTC ?? "")
            : "",
      };
      hid = historyItem.hid;
      list.push({
        f1,
        f2,
        f3,
        f4,
        hid,
        view: historyItem.nullData
          ? false
          : excludedViewActions.every(
              action => !historyItem.action?.toLowerCase().includes(action),
            ),
      });
    } else if (historyItem.rid) {
      if (historyItem.reason === "re-verify") {
        const data = historyItem.data ? JSON.parse(historyItem.data) : "";

        if (data.primaryuser) {
          f1 = "Primary Verifier";
          f2 = historyItem.rolename ? historyItem.rolename : "";
          f3 = historyItem.name ? historyItem.name : "";
          f4 = {
            date: historyItem.date,
            dateUTC:
              "dateutc" in historyItem ? (historyItem.dateutc ?? "") : "",
          };
          hid = historyItem.hid;
          list.push({ f1, f2, f3, f4, hid });
        }

        if (data.secondaryuser) {
          f1 = "Secondary Verifier";
          f2 = historyItem.rolename;
          f3 = historyItem.name;
          f4 = {
            date: historyItem.date,
            dateUTC:
              "dateutc" in historyItem || "dateUTC" in historyItem
                ? (historyItem.dateutc ?? historyItem.dateUTC ?? "")
                : "",
          };
          hid = historyItem.hid;
          list.push({ f1, f2, f3, f4, hid });
        }
      } else {
        f1 = historyItem.reason
          ? (types[historyItem.reason] ?? historyItem.reason)
          : historyItem.reason;
        f2 = historyItem.rolename;
        f3 = historyItem.full_name;
        f4 = {
          date: historyItem.date,
          dateUTC:
            "dateutc" in historyItem || "dateUTC" in historyItem
              ? (historyItem.dateutc ?? historyItem.dateUTC ?? "")
              : "",
        };
        hid = historyItem.hid;
        const pushObj: any = { f1, f2, f3, f4, hid };
        if (historyItem.reason === "entryDeletedRow" && historyItem.data) {
          pushObj.data = JSON.parse(historyItem.data);
        }

        list.push(pushObj);
      }
    } else {
      if (historyItem.primaryuser) {
        f1 = "Primary Verifier";
        f2 = historyItem.primaryrole ? historyItem.primaryrole : "";
        f3 = historyItem.primaryuser ? historyItem.primaryuser : "";
        f4 = historyItem.primarydate
          ? dayjs(historyItem.primarydate).format("D MMM YYYY - HH:mm")
          : "";
        list.push({ f1, f2, f3, f4 });
      }

      if (historyItem.secondaryuser) {
        f1 = "Secondary Verifier";
        f2 = historyItem.secondaryuser ? historyItem.secondaryuser : "";
        f3 = historyItem.secondaryrole ? historyItem.secondaryrole : "";
        f4 = historyItem.secondarydate
          ? dayjs(historyItem.secondarydate).format("D MMM YYYY - HH:mm")
          : "";
        list.push({ f1, f2, f3, f4 });
      }

      if (historyItem.auditman && historyItem.auditman instanceof Array) {
        historyItem.auditman.forEach(t => {
          f1 = getEntryStatusLabel(t.status);
          f2 = t.role ? t.role : "";
          f3 = t.name ? t.name : "";
          f4 = t.date ? t.date + " - " + t.time : "";
          audit.push({ f1, f2, f3, f4 });
        });
      }

      if (historyItem.auditdata && historyItem.auditdata instanceof Array) {
        historyItem.auditdata.forEach(t => {
          f1 = getEntryStatusLabel(t.status);
          f2 = t.role ? t.role : "";
          f3 = t.name ? t.name : "";
          f4 = t.date ? t.date + " - " + t.time : "";
          audit.push({ f1, f2, f3, f4 });
        });
      }

      if (historyItem.status === 99) {
        f1 = getEntryStatusLabel(historyItem.status);
        f2 = historyItem.rolename ? historyItem.rolename : "";
        f3 = historyItem.full_name ? historyItem.full_name : "";
        f4 = {
          date: historyItem.date,
          dateUTC: "dateutc" in historyItem ? (historyItem.dateutc ?? "") : "",
        };

        list.push({ f1, f2, f3, f4 });
      }
    }
  });

  return [...list, ...audit].sort(
    (a, b) => getRowTimestamp(b.f4) - getRowTimestamp(a.f4),
  );
};
export const headers = [
  {
    name: "type",
    label: "Type",
    style: { width: "35%" },
  },
  {
    name: "role",
    label: "Role",
    style: { width: "10%" },
  },
  {
    name: "user",
    label: "User",
    style: { width: "20%" },
  },
  {
    name: "date",
    label: "Date",
    style: { width: "30%" },
  },
  {
    name: "blank",
    label: "",
    style: { width: "5%" },
  },
];
